import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtErr } from "../api";
import { useI18n } from "../i18n";
import ShareMenu from "../components/ShareMenu";
import { PlantEvidencePanel } from "../components/PlantEvidencePanel";
import { ClaimProfileSection } from "../components/ClaimProfileSection";
import { ExternalLink } from "lucide-react";
import {
  GovListedBadge,
  PendingVerificationBadge,
  EvidenceBackedBadge,
  CorridorVerifiedBadge,
  LegacyVerifiedBadge,
  DemoBadge,
} from "./adminUiHelpers";

export default function ManufacturerPublic() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/manufacturers/slug/${slug}`);
        setM(data);
      } catch (e) {
        setErr(fmtErr(e.response?.data?.detail) || "Not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Per-page SEO: title, description, JSON-LD LocalBusiness
  useEffect(() => {
    if (!m) return;
    const tag = (lang === "es" ? m.tagline_es : m.tagline_en) || m.company_name;
    const desc = (lang === "es" ? m.description_es : m.description_en) || tag || "";
    document.title = `${m.company_name} — IMMEX manufacturer · ${m.city || "Juárez"} · Corridor Digital`;
    const meta =
      document.querySelector('meta[name="description"]') ||
      Object.assign(document.createElement("meta"), { name: "description" });
    meta.setAttribute("content", desc.slice(0, 320));
    if (!meta.parentNode) document.head.appendChild(meta);

    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: m.company_name,
      legalName: m.legal_name || undefined,
      description: desc,
      url: `https://corridordigital.net/m/${m.slug}`,
      image: m.cover_image_url || undefined,
      logo: m.logo_url || undefined,
      address: {
        "@type": "PostalAddress",
        addressLocality: m.city || "Ciudad Juárez",
        addressRegion: m.state || "Chihuahua",
        addressCountry: m.country || "MX",
      },
      email: m.contact_email || undefined,
      telephone: m.contact_phone || undefined,
      foundingDate: m.year_founded ? String(m.year_founded) : undefined,
      numberOfEmployees: m.employees || undefined,
      knowsAbout: (m.capabilities || []).slice(0, 20),
      hasCredential: (m.certifications || []).map((c) => ({
        "@type": "EducationalOccupationalCredential",
        name: c,
      })),
      identifier: [
        m.immex_number ? { "@type": "PropertyValue", name: "IMMEX", value: m.immex_number } : null,
        m.duns_number ? { "@type": "PropertyValue", name: "DUNS", value: m.duns_number } : null,
      ].filter(Boolean),
    };
    let script = document.getElementById("ld-manufacturer");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ld-manufacturer";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    return () => {
      const s = document.getElementById("ld-manufacturer");
      if (s) s.remove();
    };
  }, [m, lang]);

  if (loading) return <div className="container-cd py-20 overline">{t("common.loading")}</div>;
  if (err || !m) return <div className="container-cd py-20 overline">{err || "Not found"}</div>;

  const description = lang === "es" ? m.description_es || m.description_en : m.description_en || m.description_es;
  const tagline = lang === "es" ? m.tagline_es || m.tagline_en : m.tagline_en || m.tagline_es;
  const isImmex = (m.slug || "").startsWith("immex-");
  const hasEvidence = Array.isArray(m.capability_evidence) && m.capability_evidence.length > 0;

  return (
    <div data-testid="manufacturer-public-page">
      {m.is_demo && (
        <div className="text-center py-2.5 font-mono text-xs" style={{ background: "var(--ink)", color: "var(--orange)" }} data-testid="demo-banner">
          ⓘ EXAMPLE LISTING — This is how your factory profile could look. <a href="/register" className="underline ml-2">List yours →</a>
        </div>
      )}
      {/* HERO */}
      <section className="relative" style={{ borderBottom: "1px solid var(--line)" }}>
        {m.cover_image_url ? (
          <div
            className="absolute inset-0"
            style={{ backgroundImage: `url('${m.cover_image_url}')`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
        ) : null}
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative container-cd py-20 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-2">
            {m.logo_url ? (
              <img src={m.logo_url} alt="" className="w-full h-32 object-contain" style={{ background: "white", border: "1px solid var(--line)" }} />
            ) : (
              <div className="w-full h-32 flex items-center justify-center font-display text-4xl" style={{ background: "var(--ink)", color: "white", border: "1px solid var(--line)" }}>
                {(m.company_name || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="md:col-span-7">
            <div className="overline">
              {m.city}, {m.state} · {m.country} · MFG-{m.profile_id.slice(-6).toUpperCase()}
            </div>
            <h1 className="font-display text-5xl md:text-7xl mt-3 leading-[0.92]" data-testid="public-company-name">
              {m.company_name}
            </h1>
            <p className="mt-4 text-xl" style={{ color: "var(--ink-2)", maxWidth: 680 }}>{tagline}</p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid={`profile-trust-labels-${m.slug}`}>
              {isImmex ? <GovListedBadge lang={lang} testid={`mfg-gov-badge-${m.slug}`} /> : null}
              {m.corridor_verified === true ? (
                <CorridorVerifiedBadge testid={`mfg-corridor-badge-${m.slug}`} />
              ) : (
                <PendingVerificationBadge lang={lang} testid={`mfg-pending-badge-${m.slug}`} />
              )}
              {hasEvidence ? <EvidenceBackedBadge lang={lang} testid={`mfg-evidence-badge-${m.slug}`} /> : null}
              {m.verified === true && m.corridor_verified !== true ? (
                <LegacyVerifiedBadge testid={`mfg-legacy-badge-${m.slug}`} />
              ) : null}
              {m.is_demo === true ? <DemoBadge testid={`mfg-demo-badge-${m.slug}`} /> : null}
              {m.featured ? <span className="cd-chip cd-chip-orange">★ {t("pp.featured")}</span> : null}
              <span className="cd-chip">PROFILE VIEWS · {m.profile_views || 0}</span>
            </div>
          </div>
          <div className="md:col-span-3">
            <a href="#inquiry" className="btn btn-primary btn-lg w-full" data-testid="public-inquiry-cta">
              {t("pp.contact")} ↓
            </a>
            <div className="mt-3 flex justify-end">
              <ShareMenu
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={m.company_name}
                text={`${m.company_name} — ${m.city}, ${m.state}. IMMEX manufacturer on Corridor Digital.`}
                testid="profile-share"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CLAIM OR CONFIRM THIS PROFILE */}
      <ClaimProfileSection m={m} lang={lang} />

      {/* SPEC SHEET */}
      <section className="container-cd py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-8">
          <div className="overline">OVERVIEW · 01</div>
          <div className="mt-6 whitespace-pre-wrap text-base leading-relaxed" style={{ color: "var(--ink)" }}>
            {description || "—"}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-0" style={{ border: "1px solid var(--line)" }}>
            <SpecBlock label={t("pp.capabilities")} items={m.capabilities} />
            <SpecBlock label={t("pp.certifications")} items={m.certifications} />
            <SpecBlock label={t("pp.industries")} items={m.industries} last />
          </div>

          <EvidenceCapabilitiesSection m={m} lang={lang} />

          {(m.gallery || []).length > 0 ? (
            <div className="mt-12">
              <div className="overline">PLANT · GALLERY</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {m.gallery.map((g, i) => (
                  <img key={i} src={g} alt="" className="w-full h-56 object-cover" style={{ border: "1px solid var(--line)" }} />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {/* Sidebar */}
        <aside className="md:col-span-4 space-y-6">
          <PlantRecordCard m={m} lang={lang} />
          <div className="cd-card p-6 space-y-4">
            <div>
              <div className="overline">{t("pp.specs")}</div>
            </div>
            <SpecRow k={t("pp.location")} v={`${m.city || "—"}, ${m.state || ""}`} />
            <SpecRow k={t("pp.year_founded")} v={m.year_founded || "—"} />
            <SpecRow k={t("pp.employees")} v={m.employees || "—"} />
            <SpecRow k={t("pp.plant_size")} v={m.plant_size_sqm ? `${m.plant_size_sqm.toLocaleString()} m²` : "—"} />
            {m.verified ? (
              <>
                <SpecRow k="Website" v={m.website ? <a href={m.website} target="_blank" rel="noreferrer" className="underline">{m.website.replace(/https?:\/\//, "")}</a> : "—"} />
                <SpecRow k="Email" v={m.contact_email || "—"} />
                <SpecRow k="Phone" v={m.contact_phone || "—"} />
              </>
            ) : (
              <div
                className="border-t pt-4 mt-2 text-xs font-mono"
                style={{ borderColor: "var(--line)", color: "var(--ink-2)" }}
                data-testid="public-contact-redacted"
              >
                {lang === "es"
                  ? "Información de contacto disponible tras verificación. Envía una solicitud (RFQ) y Corridor Digital la canaliza como parte de la verificación de la planta."
                  : "Contact info available after verification. Send an RFQ below — Corridor Digital forwards it as part of plant verification."}
              </div>
            )}
          </div>

          {(m.legal_name || m.rfc || m.immex_number || m.padron_number || m.duns_number) ? (
            <div className="cd-card p-6 space-y-3" data-testid="public-legal-entity">
              <div className="flex items-center justify-between">
                <div className="overline">{t("pp.legalEntity")}</div>
                {m.verified ? (
                  <span className="cd-chip cd-chip-green" data-testid="public-verified-entity">
                    ✓ {t("pp.verifiedEntity")}
                  </span>
                ) : null}
              </div>
              {m.legal_name ? <SpecRow k={t("profile.legal_name")} v={m.legal_name} /> : null}
              {m.rfc ? <SpecRow k="RFC" v={m.rfc} /> : null}
              {m.immex_number ? <SpecRow k="IMMEX #" v={m.immex_number} /> : null}
              {m.padron_number ? <SpecRow k={t("profile.padron_number")} v={m.padron_number} /> : null}
              {m.duns_number ? <SpecRow k="D-U-N-S" v={m.duns_number} /> : null}
              <div className="font-mono text-xs" style={{ color: "var(--ink-2)", fontSize: 10 }}>
                {t("pp.rfcMaskedNote")}
              </div>
            </div>
          ) : null}

          <PlantEvidencePanel slug={slug} lang={lang} />
        </aside>
      </section>

      {/* INQUIRY FORM */}
      <section
        id="inquiry"
        className="section-cd"
        style={{ background: "var(--ink)", color: "white", borderTop: "1px solid var(--line)" }}
      >
        <div className="container-cd grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>RFQ · 02</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[0.95]">
              {t("pp.contact")}
            </h2>
            <p className="mt-6" style={{ color: "rgba(255,255,255,0.7)", maxWidth: 360 }}>
              {m.company_name} responds in {m.languages?.join(" / ") || "ES / EN"}.
            </p>
          </div>
          <div className="md:col-span-7">
            <InquiryForm slug={m.slug} companyName={m.company_name} t={t} />
          </div>
        </div>
      </section>
    </div>
  );
}

function SpecRow({ k, v }) {
  return (
    <div className="flex justify-between items-baseline gap-4 font-mono text-xs">
      <div className="overline" style={{ color: "var(--ink-2)" }}>{k}</div>
      <div className="text-right text-sm" style={{ fontFamily: "'IBM Plex Sans'", color: "var(--ink)" }}>
        {v}
      </div>
    </div>
  );
}

/* Plant record facts — official listing data only, nothing invented.
 * Empty fields render as "—" / "Not submitted". */
function PlantRecordCard({ m, lang }) {
  const address = m.plant_address || m.street_address || m.address || "";
  const postal = m.postal_code || m.zip_code || "";
  const immexRef = m.immex_program_reference || m.immex_number || "";
  const govActivity = m.government_official_activity_es || "";
  return (
    <div className="cd-card p-6 space-y-4" data-testid="plant-record-card">
      <div className="overline">{lang === "es" ? "REGISTRO DE PLANTA" : "PLANT RECORD"}</div>
      <SpecRow
        k={lang === "es" ? "Secuencia de planta" : "Plant sequence"}
        v={m.plant_sequence_label || "—"}
      />
      <SpecRow
        k={lang === "es" ? "Dirección" : "Address"}
        v={
          address
            ? `${address}${postal ? ` · ${postal}` : ""}`
            : lang === "es" ? "No enviada" : "Not submitted"
        }
      />
      {m.assigned_park ? (
        <SpecRow k={lang === "es" ? "Parque" : "Park"} v={m.assigned_park} />
      ) : null}
      <SpecRow
        k="IMMEX"
        v={immexRef ? `${immexRef}${m.immex_program_year ? ` · ${m.immex_program_year}` : ""}` : "—"}
      />
      {govActivity ? (
        <div data-testid="plant-record-gov-activity">
          <div className="overline" style={{ color: "var(--ink-2)" }}>
            {lang === "es" ? "Actividad oficial (gobierno)" : "Official government activity (ES)"}
          </div>
          <div className="mt-1 text-sm" style={{ color: "var(--ink)" }}>{govActivity}</div>
        </div>
      ) : null}
      <div
        className="border-t pt-3 font-mono text-xs"
        style={{ borderColor: "var(--line)", color: "var(--ink-2)" }}
        data-testid="plant-record-basis-note"
      >
        {lang === "es"
          ? "La información se basa en el listado oficial IMMEX y en evidencia de investigación citada. La verificación Corridor específica de planta está pendiente salvo que el perfil muestre Corridor Verified."
          : "Information is based on the official IMMEX listing and cited research evidence. Plant-specific Corridor verification is pending unless the profile shows Corridor Verified."}
      </div>
    </div>
  );
}

/* Evidence-backed capabilities — plant-specific research evidence only.
 * Unconfirmed company-level evidence is never displayed here. */
function EvidenceCapabilitiesSection({ m, lang }) {
  const rows = Array.isArray(m.capability_evidence) ? m.capability_evidence : [];
  if (rows.length === 0) return null;
  const groups = new Map();
  for (const r of rows) {
    const tag = r.capability_tag || "—";
    if (!groups.has(tag)) groups.set(tag, []);
    groups.get(tag).push(r);
  }
  return (
    <div className="mt-12" data-testid="evidence-capabilities-section">
      <div className="overline">
        {lang === "es" ? "CAPACIDADES RESPALDADAS POR EVIDENCIA" : "EVIDENCE-BACKED CAPABILITIES"}
      </div>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }} data-testid="evidence-section-note">
        {lang === "es"
          ? "Solo evidencia de investigación específica de esta planta. La evidencia a nivel empresa sin confirmar nunca se muestra como capacidad de planta."
          : "Plant-specific research evidence only. Unconfirmed company-level evidence is never displayed as a plant capability."}
      </p>
      <div className="mt-4 space-y-4">
        {[...groups.entries()].map(([tag, items]) => (
          <div
            key={tag}
            className="cd-card p-5"
            data-testid={`evidence-cap-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="cd-chip" style={{ background: "#ccfbf1", color: "#115e59", borderColor: "#5eead4" }}>
                {tag}
              </span>
            </div>
            {items.map((ev, i) => (
              <div
                key={i}
                className="mt-3 pt-3"
                style={{ borderTop: i > 0 ? "1px dashed var(--line)" : "none" }}
              >
                <blockquote
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--ink)", fontStyle: "italic" }}
                  data-testid={`evidence-quote-${i}`}
                >
                  “{ev.exact_supporting_text}”
                </blockquote>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  {ev.evidence_source_url ? (
                    <a
                      href={ev.evidence_source_url}
                      target="_blank"
                      rel="noreferrer nofollow"
                      className="inline-flex items-center gap-1 underline"
                      style={{ color: "var(--ink)" }}
                      data-testid={`evidence-source-link-${i}`}
                    >
                      <ExternalLink size={11} strokeWidth={1.8} />
                      {(() => { try { return new URL(ev.evidence_source_url).hostname; } catch { return ev.evidence_source_url; } })()}
                    </a>
                  ) : null}
                  {ev.source_type ? <span>{ev.source_type}</span> : null}
                  {ev.confidence ? (
                    <span>
                      {lang === "es" ? "Confianza" : "Confidence"}: {ev.confidence}
                    </span>
                  ) : null}
                  {ev.date_checked ? (
                    <span>
                      {lang === "es" ? "Consultado" : "Checked"}: {ev.date_checked}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecBlock({ label, items, last }) {
  return (
    <div className="p-6" style={{ background: "white", borderRight: last ? "none" : "1px solid var(--line)" }}>
      <div className="overline">{label}</div>
      <div className="tag-list mt-4">
        {(items || []).length === 0 ? (
          <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>—</span>
        ) : (
          items.map((c, i) => <span key={i} className="cd-chip">{c}</span>)
        )}
      </div>
    </div>
  );
}

function InquiryForm({ slug, companyName, t }) {
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_company: "",
    buyer_phone: "",
    subject: `Inquiry for ${companyName}`,
    message: "",
    quantity: "",
    timeline: "",
  });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  function update(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/inquiries", { manufacturer_slug: slug, ...form });
      setSent(true);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="cd-card p-8" style={{ background: "white", color: "var(--ink)" }} data-testid="inquiry-sent">
        <div className="overline" style={{ color: "var(--orange)" }}>SENT</div>
        <h3 className="font-display text-3xl mt-3">{t("pp.sent.title")}</h3>
        <p className="mt-3" style={{ color: "var(--ink-2)" }}>{t("pp.sent.body")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-4" data-testid="inquiry-form">
      <FormField label={t("pp.your_name")} value={form.buyer_name} onChange={(v) => update("buyer_name", v)} required testid="inq-name" />
      <FormField label={t("pp.your_email")} value={form.buyer_email} onChange={(v) => update("buyer_email", v)} type="email" required testid="inq-email" />
      <FormField label={t("pp.your_company")} value={form.buyer_company} onChange={(v) => update("buyer_company", v)} testid="inq-company" />
      <FormField label="Phone" value={form.buyer_phone} onChange={(v) => update("buyer_phone", v)} testid="inq-phone" />
      <FormField label={t("pp.subject_h")} value={form.subject} onChange={(v) => update("subject", v)} required testid="inq-subject" full />
      <FormField label={t("pp.quantity_h")} value={form.quantity} onChange={(v) => update("quantity", v)} testid="inq-quantity" />
      <FormField label={t("pp.timeline_h")} value={form.timeline} onChange={(v) => update("timeline", v)} testid="inq-timeline" />
      <div className="md:col-span-2">
        <label className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>{t("pp.message_h")}</label>
        <textarea
          required
          className="cd-textarea mt-2"
          style={{ background: "white", color: "var(--ink)" }}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={5}
          data-testid="inq-message"
        />
      </div>
      {err ? (
        <div className="md:col-span-2 font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }}>
          {err}
        </div>
      ) : null}
      <div className="md:col-span-2">
        <button type="submit" className="btn btn-primary btn-lg" disabled={loading} data-testid="inq-submit">
          {loading ? "…" : t("pp.send")}
        </button>
      </div>
    </form>
  );
}

function FormField({ label, value, onChange, type = "text", required, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>{label}</label>
      <input
        className="cd-input mt-2"
        style={{ background: "white", color: "var(--ink)", paddingLeft: 10, paddingRight: 10, borderBottom: "2px solid var(--ink)" }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        required={required}
        data-testid={testid}
        data-testid-alt={testid?.replace("inq-", "inquiry-")}
        name={testid?.replace("inq-", "inquiry-")}
      />
    </div>
  );
}